
import Vue from 'vue'
import promptConfig from '~/config/home-prompt.json'
import AppActions from '~/domain/constants/AppActions'
import { openNewTabFallback } from '~/domain/helpers/helpers'

interface PromptTag {
  id: string
  label: string
  placeholders: string[]
}

export default Vue.extend({
  name: 'HomePrompt',
  data() {
    return {
      availableTags: promptConfig as PromptTag[],
      selectedTagIds: [] as string[],
      currentPlaceholder: '',
      userInput: '',
      isFocused: false,
      typewriter: {
        text: '',
        isDeleting: false,
        index: 0,
        fullText: '',
        timer: null as any,
      },
    }
  },
  computed: {
    activePlaceholders(): string[] {
      if (this.selectedTagIds.length === 0) {
        return this.availableTags.reduce((acc, tag) => [...acc, ...tag.placeholders], [] as string[])
      }
      return this.availableTags
        .filter(tag => this.selectedTagIds.includes(tag.id))
        .reduce((acc, tag) => [...acc, ...tag.placeholders], [] as string[])
    },
    userIsAuthenticated(): boolean {
      return this.$store.getters.userIsAuthenticated
    },
  },
  watch: {
    activePlaceholders(newPlaceholders) {
      const isCurrentValid = newPlaceholders.includes(this.typewriter.fullText)
      if (!isCurrentValid && this.typewriter.fullText) {
        this.forceResetTypewriter()
      }
    }
  },
  mounted() {
    this.loadSelectedTags()
    this.startTypewriter()
    window.addEventListener('resize', this.adjustTextareaHeight)
  },
  beforeDestroy() {
    this.stopTypewriter()
    window.removeEventListener('resize', this.adjustTextareaHeight)
  },
  methods: {
    loadSelectedTags() {
      if (process.client) {
        const storedTags = localStorage.getItem('machinations_hero_tags')
        if (storedTags) {
          try {
            this.selectedTagIds = JSON.parse(storedTags)
          } catch (e) {
            console.error('Failed to parse tags', e)
          }
        }
      }
    },
    saveSelectedTags() {
      if (process.client) {
        localStorage.setItem('machinations_hero_tags', JSON.stringify(this.selectedTagIds))
      }
    },
    isTagSelected(tagId: string) {
      return this.selectedTagIds.includes(tagId)
    },
    toggleTag(tagId: string) {
      if (this.isTagSelected(tagId)) {
        this.selectedTagIds = []
      } else {
        this.selectedTagIds = [tagId]
      }
      this.saveSelectedTags()
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.heroInput as HTMLTextAreaElement
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    },
    startTypewriter() {
      this.typewriterLoop()
    },
    stopTypewriter() {
      if (this.typewriter.timer) {
        clearTimeout(this.typewriter.timer)
      }
    },
    focusPrompt() {
      const textarea = this.$refs.heroInput as HTMLTextAreaElement
      if (textarea) {
        textarea.focus()
      }
    },
    forceResetTypewriter() {
      this.typewriter.isDeleting = true
      this.stopTypewriter()
      this.typewriterLoop()
    },
    typewriterLoop() {
      const placeholders = this.activePlaceholders.length > 0 ? this.activePlaceholders : [this.$t('homePromptPlaceholderFallback') as string]
      
      const { index } = this.typewriter
      let { fullText, isDeleting } = this.typewriter

      if (!fullText || (isDeleting && this.currentPlaceholder === '')) {
        this.typewriter.isDeleting = false
        isDeleting = false
        this.typewriter.index = (index + 1) % placeholders.length
        fullText = placeholders[this.typewriter.index]
        this.typewriter.fullText = fullText
      }
      
      const currentLength = this.currentPlaceholder.length
      const fullTextLength = fullText.length
      let step = 1
      if (isDeleting) {
        step += Math.floor((fullTextLength - currentLength)/10)
        this.currentPlaceholder = fullText.substring(0, Math.max(0, currentLength - step))
      } else {
        step += Math.floor(currentLength/10)
        this.currentPlaceholder = fullText.substring(0, Math.min(fullTextLength, currentLength + step))
      }

      let delta = 100 - Math.random() * 50
      if (isDeleting) {
        delta /= 3
      }
      if (!isDeleting && this.currentPlaceholder === fullText) {
        delta = 2500
        this.typewriter.isDeleting = true
      } else if (isDeleting && this.currentPlaceholder === '') {
        delta = 500
      } else if (isDeleting && !this.activePlaceholders.includes(fullText)) {
        delta /= 4
      }

      this.typewriter.timer = setTimeout(() => {
        this.typewriterLoop()
      }, delta)
    },
    handleFocus() {
      this.isFocused = true
      this.stopTypewriter()
      
      if (!this.userInput && this.typewriter.fullText) {
        this.userInput = this.typewriter.fullText
        this.currentPlaceholder = '' 
        this.$nextTick(() => {
          this.adjustTextareaHeight()
        })
      }
      
      // Use setTimeout to ensure we override the browser's click-to-focus cursor placement
      setTimeout(() => {
        const textarea = this.$refs.heroInput as HTMLTextAreaElement
        if (textarea) {
          const len = this.userInput.length
          textarea.focus()
          textarea.setSelectionRange(len, len)
        }
      }, 0)
    },
    handleBlur() {
      if (this.userInput === this.typewriter.fullText) {
        this.currentPlaceholder = this.userInput
        this.userInput = ''
        this.typewriter.isDeleting = true
        this.$nextTick(() => {
          this.adjustTextareaHeight()
        })
      }
      
      this.isFocused = false
      this.startTypewriter()
    },
    handleSubmit() {
      const promptText = this.userInput || this.typewriter.fullText
      if (!promptText) return

      const baseUrl = this.userIsAuthenticated ? this.$config.apiBaseURL : `${this.$config.apiBaseURL}/register`
      const destinationUrl = `${baseUrl}?prompt=${encodeURIComponent(promptText)}`
      
      this.$store.dispatch(AppActions.TRACK_SIGN_UP_EVENT, {
        context: this,
        location: 'homepage_hero_prompt',
        link: destinationUrl,
        prompt: promptText,
      }).then(() => {
        openNewTabFallback(destinationUrl)
      })
    }
  }
})
